import './accountMenu.scss';
import React, { useContext, useMemo, useCallback } from 'react';
import { doLogOut } from '../../../../services/authorizationService';
import { PlainText } from '../../../plainText/plainText';
import { AppContext } from '../../../../context/appContext';
import { ENTERPRISE_ROLE } from '@umu/umu-types';
import { isEnterprise } from '../../../../utils/util';
import LangCache from '../../../../base/langCache';

export interface IProps {}

enum PlatePermissionType {
    UMU = 1,
    USE_ONLY,
    UMU_AND_USE,
}

export function AccountMenu(props: IProps) {
    const { state } = useContext(AppContext);
    const {
        userInfo,
        enterpriseInfo,
        isInUSE,
        isOnlyUseEnterpriseAccount,
        isOnlyUmuEnterpriseAccount,
        isUseAndUmuEnterpriseAccount,
        profileUrl,
        isInAITools,
    } = state;
    const { role_type } = enterpriseInfo;

    const platform_permission = (userInfo as any).platform_permission;
    const isEnterpriseAccount = isEnterprise(enterpriseInfo.enterprise_id);
    const userPermission = useMemo(() => {
        return {
            isOnlyUseEnterpriseAccount:
                isOnlyUseEnterpriseAccount || platform_permission === PlatePermissionType.USE_ONLY,
            isOnlyUmuEnterpriseAccount: isOnlyUmuEnterpriseAccount || platform_permission === PlatePermissionType.UMU,
            isUseAndUmuEnterpriseAccount:
                isUseAndUmuEnterpriseAccount || platform_permission === PlatePermissionType.UMU_AND_USE,
        };
    }, [isOnlyUseEnterpriseAccount, isOnlyUmuEnterpriseAccount, isUseAndUmuEnterpriseAccount, platform_permission]);

    const hasUSEPermission = useMemo(
        () => userPermission.isOnlyUseEnterpriseAccount || userPermission.isUseAndUmuEnterpriseAccount,
        [userPermission.isOnlyUseEnterpriseAccount, userPermission.isUseAndUmuEnterpriseAccount],
    );
    const hasUMUPermission = useMemo(
        () => userPermission.isOnlyUmuEnterpriseAccount || userPermission.isUseAndUmuEnterpriseAccount,
        [userPermission.isOnlyUmuEnterpriseAccount, userPermission.isUseAndUmuEnterpriseAccount],
    );

    const LANG = LangCache.getLang();

    const logout = useCallback((evt: React.MouseEvent) => {
        evt.stopPropagation();
        evt.preventDefault();

        doLogOut();
    }, []);

    const renderToProfile = useCallback(() => {
        if (isInUSE) {
            return null;
        }
        return (
            <div className="action link-action to-profile">
                <a href={profileUrl || '/profile#/profile'} target="_blank" rel="noreferrer" className="btn">
                    <PlainText>个人中心</PlainText>
                </a>
            </div>
        );
    }, []);

    const renderToUSE = useCallback(() => {
        const display = !isInUSE && hasUSEPermission;
        if (!display) {
            return null;
        }
        return (
            <div className="action link-action to-profile">
                <a href="/use" target="_blank" className="btn">
                    <PlainText>去赋能平台</PlainText>
                </a>
            </div>
        );
    }, [isInUSE, hasUSEPermission]);

    const renderToUMU = useCallback(() => {
        const display = isInUSE && hasUMUPermission && !isInAITools;
        if (!display) {
            return null;
        }
        const domain = (window as any)?.pageData?.config?.site_host || location.hostname;
        return (
            <div className="action link-action to-profile">
                <a href={`https://${domain}/index`} target="_blank" rel="noreferrer" className="btn">
                    <PlainText>去学习平台</PlainText>
                </a>
            </div>
        );
    }, [isInUSE, hasUMUPermission]);

    const renderToPlatform = useCallback(() => {
        const roleList = [
            ENTERPRISE_ROLE.ADMIN,
            ENTERPRISE_ROLE.A_ADMIN,
            ENTERPRISE_ROLE.SUB_ADMIN,
            ENTERPRISE_ROLE.DIRECTOR,
            ENTERPRISE_ROLE.TEACHER,
        ];
        const display = isInUSE && roleList.includes(Number(role_type) as ENTERPRISE_ROLE);
        if (!display) {
            return null;
        }
        const adminList = [ENTERPRISE_ROLE.ADMIN, ENTERPRISE_ROLE.SUB_ADMIN, ENTERPRISE_ROLE.A_ADMIN];
        // 管理员进入管理后台，讲师和学习负责人进入我的课程
        const url = adminList.includes(Number(role_type) as ENTERPRISE_ROLE)
            ? isInAITools
                ? '/ai-tools/manage/dashboard'
                : '/platformManage/index'
            : '/course/index';
        return (
            <div className="action link-action to-profile">
                <a href={url} target="_blank" rel="noreferrer" className="btn">
                    <PlainText>管理后台</PlainText>
                </a>
            </div>
        );
    }, [isInUSE, role_type]);

    // 升级企业版
    const renderToUpgrade = useCallback(() => {
        // B 端用户 或者 不在 AITools 页面中不展示
        if (isEnterpriseAccount || !isInAITools) {
            return null;
        }
        return (
            <div className="action link-action to-profile">
                <a href="/cms/contact-sales" target="_blank" rel="noreferrer" className="btn">
                    <PlainText>{LANG['升级企业版']}</PlainText>
                </a>
            </div>
        );
    }, [isEnterpriseAccount, isInAITools]);

    // 购买 AI Tools 点数
    const renderToBuyPoints = useCallback(() => {
        // B 端用户 或者 不在 AITools 页面中不展示
        if (isEnterpriseAccount || !isInAITools) {
            return null;
        }
        return (
            <div className="action link-action to-profile">
                <a href="/ai-tools/payment" target="_blank" rel="noreferrer" className="btn">
                    <PlainText>{LANG['购买点数']}</PlainText>
                </a>
            </div>
        );
    }, [isEnterpriseAccount, isInAITools]);

    return (
        <div className="current-account-actions">
            {renderToProfile()}
            {renderToUSE()}
            {renderToUMU()}
            {renderToPlatform()}
            {renderToUpgrade()}
            {renderToBuyPoints()}
            <div className="action to-logout">
                <a className="btn" onClick={logout}>
                    <PlainText>退出账户</PlainText>
                </a>
            </div>
        </div>
    );
}
